import React, { useState, useEffect, useRef, useContext } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

/** Context Files */
import DarkModeContext from "../../../utils/Context/DarkModeContext";

/** RTK configurations */
import { useDispatch, useSelector } from "react-redux";
import { setProjectData } from "../../../app/slice/projectSlice";

/** Utilities */
import { getCookie } from "../../../utils/Cookies/CookieHandler";

/** Common Components */
import Iconify from "../iconify/iconify";
import Image from "../../../components/image/Image";


/** Configuration Files */
import {
  Menu,
  ProjectMenu,
  AdminMenu,
  TeamsMenu,
  MyProjectMenu,
  MyTeamsMenu,
} from "../menu/Sidebarmenu";

/** CSS Files */
import "./Sidebar.scss";
import { hasPermission } from "../../../utils/AuthorizeNavigation/authorizeNavigation";
import { useLazyGetOrganizationByIdQuery } from "../../../app/services/organizationAPI";
import moltech_logo from "../../../assets/images/moltech_logo.png"

const Sidebar = ({ sidebarCollapsed, toggleSidebarCollapsed }) => {
  const { isDarkMode, toggleDarkMode } = useContext(DarkModeContext);

  const navigate = useNavigate();
  const location = useLocation();
  const dispath = useDispatch();

  const menuItem = location.pathname;
  const { id } = useParams();

  const cookie = getCookie("AuthUser");
  const userID = cookie?.user?.lUserKey;
  const { user } = useSelector((state) => state.auth);
  const { projectData } = useSelector((state) => state.project);
  
  const sidebarRef = useRef(null);

  const [selectedMenu, setSelectedMenu] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [organizationLogo, setOrganizationLogo] = useState("");
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [selectedSubMenu, setSelectedSubMenu] = useState(null);
  const [navigationMenuList, setNavigationMenuList] = useState(null);
  const [isCustomSidebarVisible, setIsCustomSidebarVisible] = useState(false);

  const [getOrganizationById, {
    isSuccess: organizationSuccess,
    data: organizationData,
  },] = useLazyGetOrganizationByIdQuery();

  useEffect(() => {
    if (user) {
      getOrganizationById(user.organizationId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (organizationSuccess && organizationData) {

      setOrganizationLogo(organizationData.organizationPicBase64 ? `data:image/png;base64,${organizationData.organizationPicBase64}` : moltech_logo
      );
    }
  }, [organizationSuccess, organizationData]);

  useEffect(() => {
    if (projectData) {
      setProjectName(projectData.projectName);
    } else {
      setProjectName("");
    }
  }, [projectData]);

  useEffect(() => {
    if (cookie.isAuthenticated) {
      setNavigationMenuList(Menu);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userID, cookie.isAuthenticated]);

  useEffect(() => {
    switch (true) {
      case menuItem.startsWith("/admin/project/"):
        setIsCustomSidebarVisible(true);
        setdynamicRoutes(ProjectMenu);
        break;
      case menuItem.startsWith("/admin/teams/"):
        setIsCustomSidebarVisible(true);
        setdynamicRoutes(TeamsMenu);
        break;
      case menuItem.startsWith("/admin/"):
        setIsCustomSidebarVisible(true);
        setdynamicRoutes(AdminMenu);
        break;
      case menuItem.startsWith("/myprojects/"):
        setIsCustomSidebarVisible(true);
        setdynamicRoutes(MyProjectMenu);
        break;
      case menuItem.startsWith("/myteams/"):
        setIsCustomSidebarVisible(true);
        setdynamicRoutes(MyTeamsMenu);
        break;
      default:
        dispath(setProjectData(null));
        setIsCustomSidebarVisible(false);
        setNavigationMenuList(Menu);
        break;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItem]);

  useEffect(() => {
    const currentMenu = navigationMenuList?.find((menu) =>
      location.pathname.startsWith(menu.navigationPath)
    );
    if (currentMenu) {
      setSelectedMenu(currentMenu.menuKey);
    }
  }, [location.pathname, navigationMenuList]);

  const setdynamicRoutes = (subMenu) => {
    const dynamicRoutes = subMenu.map((route) => ({
      ...route,
      navigationPath: route.navigationPath?.replace(":id", id),
      children: route.children?.map((subRoute) => ({
        ...subRoute,
        navigationPath: subRoute.navigationPath.replace(":id", id),
      })),
    }));
    setNavigationMenuList(dynamicRoutes);
  };

  const handleClick = (menuKey, e, customsidebar) => {
    if (e) e.stopPropagation();
    if (selectedMenu === menuKey) {
      setIsSubMenuOpen(!isSubMenuOpen);
    } else {
      setSelectedMenu(menuKey);
      setIsSubMenuOpen(true);
    }
    setSelectedSubMenu(null);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubMenuClick = (subMenuKey, e) => {
    if (e) e.stopPropagation();
    setSelectedSubMenu(subMenuKey);
  };

  const handleClickOutside = (event) => {
    if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
      setSelectedSubMenu(null);
      setIsSubMenuOpen(false);
    }
  };

  const handleBackButtonClick = () => {
    setIsCustomSidebarVisible(false);
    switch (true) {
      case menuItem.startsWith("/project/"):
        navigate("/projects");
        break;
      case menuItem.startsWith("/teams/"):
        navigate("/teams");
        break;
      case menuItem.startsWith("/admin/project/"):
        navigate("/admin/projects");
        break;
      case menuItem.startsWith("/admin/teams/"):
        navigate("/admin/teams");
        break;
      case menuItem.startsWith("/myprojects/"):
        navigate("/myprojects");
        break;
      case menuItem.startsWith("/myteams/"):
        navigate("/myteams");
        break;
      case menuItem.startsWith("/admin/"):
        navigate("/myprojects");
        break;
      default:
        dispath(setProjectData(null));
        setIsCustomSidebarVisible(false);
        setNavigationMenuList(Menu);
        break;
    }
    setNavigationMenuList(Menu);
  };

  const sidebarClass = isDarkMode ? "sidebar-dark" : "sidebar-light";

  return (
    <div
      className={`sidebar-section ${sidebarCollapsed ? "collapsed" : ""
        } ${sidebarClass}`}
      ref={sidebarRef}
    >
      <nav className="sidebar">
        <div className="main-menus">
          <div className="sidebar-logo">
            <Link to={"/Dashboard"} className="header-logo">
              <Image imagePath={organizationLogo} altText="Logo" />
            </Link>
            <Link to={"/Dashboard"} className="header-logo-collapse">
              {/* <Image imagePath={AppIcons.MoltechlogoSmall} altText="Logo" /> */}
              <Image imagePath={organizationLogo} altText="Logo" />
            </Link>
            <div className="icon" onClick={toggleSidebarCollapsed}>
              <Iconify icon="iconamoon:menu-burger-horizontal-fill" />
            </div>
          </div>
          {/* <div className="sidebar-searchbar">
            <SearchBar searchText="Search" />
          </div> */}
          {isCustomSidebarVisible &&
            projectName &&
            menuItem.startsWith("/project/") ? (
            <Link to={"#"} className="sidebar-brand">
              {/* <Image imagePath={AppIcons.logoImage} altText="Logo" /> */}

              <div className="sidebar-content">
                <span
                  className={
                    sidebarCollapsed ? "slide-in-right" : "slide-in-left"
                  }
                >
                  {projectName}
                </span>
              </div>
            </Link>
          ) : null}
          <div className="sidebar-menu">
            {isCustomSidebarVisible && (
              <div className="sidebar-header">
                <div
                  className={`back-icon ${sidebarCollapsed ? "collapsed" : ""}`}
                  onClick={handleBackButtonClick}
                >
                  <Iconify
                    width={24}
                    className="sidebar-icon"
                    icon="lets-icons:back"
                  />
                  <span>Back to Menu</span>
                </div>
              </div>
            )}

            <ul className="sidebar-menu-list">
              {navigationMenuList?.map((menuItem, index) => {
                if (menuItem.groupLabel) {
                  return (
                    <li key={`group-${index}`} className="menu-group-label">
                      <span>{menuItem.groupLabel}</span>
                    </li>
                  );
                }
                return (
                  hasPermission(menuItem.securityKey) && (
                    // return menuItem.isAdmin === isAdmin || !menuItem.isAdmin ? (
                    <li
                      key={index}
                      className={`menu-item ${selectedMenu === menuItem.menuKey ? "active-menu" : ""
                        }`}
                      onClick={(e) => {
                        handleClick(
                          menuItem.menuKey,
                          e,
                          menuItem.customsidebar
                        );
                      }}
                    >
                      <Link
                        to={menuItem.navigationPath}
                        className={`${menuItem.children ? "menu-arrow" : ""}`}
                      >
                        <Iconify
                          width={24}
                          className="sidebar-icon"
                          icon={menuItem.iconifyIcon}
                        />
                        <span
                          className={
                            sidebarCollapsed
                              ? "slide-in-right"
                              : "slide-in-left"
                          }
                        >
                          {menuItem.menuTitle}
                        </span>
                      </Link>

                      {selectedMenu === menuItem?.menuKey && menuItem.children && isSubMenuOpen && (
                        <ul className="sidebar-dropdown">
                          {menuItem.children.map((item, subIndex) => {
                            return (
                              hasPermission(item.securityKey) && (
                                <li
                                  key={subIndex}
                                  className={`dropdown-menus ${selectedSubMenu === item.menuKey ? "active-submenu" : ""
                                    }`}
                                  onClick={(e) => {
                                    handleSubMenuClick(item.id, e);
                                  }}
                                >
                                  <Link to={item.navigationPath}>
                                    <span
                                      className={sidebarCollapsed ? "slide-in-right" : "slide-in-left"}
                                    >
                                      {item.subMenuName}
                                    </span>
                                  </Link>
                                </li>
                              )
                            );
                          })}
                        </ul>
                      )}

                    </li>
                    // ):null
                  )
                );
              })}
            </ul>
          </div>
        </div>

        <div className="footer-menu">
          <div className="toggle-switch">
            <span className="toggle-text">
              {isDarkMode ? "Dark Mode" : "Light Mode"}
            </span>
            <input
              type="checkbox"
              id="darkModeToggle"
              checked={isDarkMode}
              onChange={toggleDarkMode}
            />
            <label htmlFor="darkModeToggle" className="toggle-label">
              <span className="toggle-button"></span>{" "}
            </label>
          </div>
          {/* <div className="sidebar-logout-menu" onClick={handleLogout}>
            <div className="account-section logout-sec">
              <Iconify
                width={24}
                className="sidebar-icon"
                icon="tabler:logout-2"
              />
              <div className="account-info">
                <div className="user-name">Log out</div>
              </div>
            </div>
          </div> */}
          {/* <div className="account-section"> */}
          {/* <Avatar alt="Vaibhav Waghela" size="medium" shape="circle" />
            <div className="account-info">
              <div className="user-name">{user.userName}</div>
              <div className="user-email">{user.emailAddress}</div>
            </div> */}
          {/* </div> */}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar;
